import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./AutoBotModal.module.css";
import { useGame } from "../store/game";
import { useHaptic } from "../hooks/useHaptic";
import { formatInt } from "../lib/format";

const HOUR_OPTIONS = [1, 4, 8, 24] as const;
const PRICE_PER_HOUR = 100;

export function AutoBotModal() {
  const activeModal = useGame((s) => s.activeModal);
  const closeModal = useGame((s) => s.closeModal);
  const tokens = useGame((s) => s.tokens);
  const autoBotActive = useGame((s) => s.autoBotActive);
  const autoBotHoursLeft = useGame((s) => s.autoBotHoursLeft);
  const autoBotBetAmount = useGame((s) => s.autoBotBetAmount);
  const startAutoBot = useGame((s) => s.startAutoBot);
  const stopAutoBot = useGame((s) => s.stopAutoBot);
  const fetchAutoBotStatus = useGame((s) => s.fetchAutoBotStatus);
  const betAmount = useGame((s) => s.betAmount);
  const haptic = useHaptic();

  const [selectedHours, setSelectedHours] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const open = activeModal === "autobot";

  useEffect(() => {
    if (open) fetchAutoBotStatus();
  }, [open, fetchAutoBotStatus]);

  const cost = selectedHours * PRICE_PER_HOUR;
  const canAfford = tokens >= cost;

  const handleStart = async () => {
    if (!canAfford) { haptic.notify("error"); return; }
    haptic.impact("heavy");
    setLoading(true);
    const ok = await startAutoBot(selectedHours, betAmount);
    setLoading(false);
    if (ok) { haptic.notify("success"); closeModal(); }
    else haptic.notify("error");
  };

  const handleStop = async () => {
    haptic.impact("medium");
    await stopAutoBot();
    haptic.notify("warning");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className={styles.backdrop} onClick={closeModal}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }} />

          <motion.div className={styles.sheet}
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 36 }}
            drag="y" dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => { if (info.offset.y > 100 || info.velocity.y > 500) closeModal(); }}
          >
            <div className={styles.handle} />

            <div className={styles.header}>
              <h2 className={styles.title}>🤖 Авто-бот</h2>
              <p className={styles.subtitle}>
                Бот ставить замість тебе поки ти не в грі.
                Випадкові ставки у {formatInt(betAmount > 0 ? betAmount : 100)} USD кожні 5 секунд.
              </p>
            </div>

            {autoBotActive ? (
              <div className={styles.statusCard}>
                <span className={styles.statusIcon}>🤖</span>
                <div className={styles.statusText}>
                  <div className={styles.statusLabel}>Активний</div>
                  <div className={styles.statusValue}>
                    {autoBotHoursLeft} год лишилось · {autoBotBetAmount} USD/ставка
                  </div>
                </div>
                <button type="button" className={styles.stopBtn} onClick={handleStop}>
                  Зупинити
                </button>
              </div>
            ) : (
              <div className={styles.config}>
                <div className={styles.configRow}>
                  <div className={styles.configLabel}>Тривалість роботи</div>
                  <div className={styles.hoursGrid}>
                    {HOUR_OPTIONS.map((h) => (
                      <button key={h} type="button"
                        className={`${styles.hourBtn} ${selectedHours === h ? styles.hourBtnActive : ""}`}
                        onClick={() => { haptic.select(); setSelectedHours(h); }}
                      >
                        {h}г
                      </button>
                    ))}
                  </div>
                </div>

                <div className={styles.costRow}>
                  <span className={styles.costLabel}>Вартість</span>
                  <span className={styles.costValue}>{cost} GT</span>
                </div>

                <button type="button" className={styles.startBtn}
                  onClick={handleStart} disabled={!canAfford || loading}
                >
                  {loading ? "Запуск..." : canAfford ? `Запустити на ${selectedHours} год 🤖` : `Недостатньо GT (є ${tokens})`}
                </button>

                <p className={styles.priceNote}>
                  {PRICE_PER_HOUR} GT/год · бот зупиняється коли баланс вичерпано
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
